import { Injectable } from '@angular/core';
import { IOptionsApprovalResponseModel } from '../interfaces/options-approval-response.model';
import { IOptionResponseModel } from '../interfaces/option-response.model';
import { IOptionsAttributeValueResponseModel } from '../interfaces/options-attribute-value-response.model';
import { IBudgetResponseModel } from '../interfaces/budget-response.model';
import { ISubTimeOptionActualPercentageRequestModel } from '../interfaces/sub-time-option-actual-percentage-request.model';
import { flatMap, orderBy } from 'lodash';
import { IMonthlyOptionColumn, IMonthlyOptionRow } from '../interfaces/monthly-option-report.interface';

@Injectable({
    providedIn: 'root'
})
export class MapsCalculationService {
    public allSkusAreZero(row: IMonthlyOptionRow): boolean {
        if (row) {
            for (const [key, value] of Object.entries(row.sku_plans)) {
                if (value !== 0 && key !== 'sku_plan' && key !== 'Total') {
                    return false;
                }
            }
        }

        return true;
    }

    public calculateAndTruncateRowSkus(row: IMonthlyOptionRow, columns: Array<IMonthlyOptionColumn>): IMonthlyOptionRow {
        const calculatedRow: IMonthlyOptionRow = structuredClone(row);
        const yearlyOptionSku: number = Number(calculatedRow.sku_plans.sku_plan);

        // Multiply the flow perc column against the yearly sku value and truncate the result
        Object.keys(calculatedRow.sku_plans).forEach((key: string) => {
            if (key.toLowerCase() !== 'sku_plan' && key.toLowerCase() !== 'total' && calculatedRow.sku_plans.sku_plan) {
                const column: IMonthlyOptionColumn = columns.find((value: IMonthlyOptionColumn) => value.column_name === key);

                if (column) {
                    calculatedRow.sku_plans[key] = Math.trunc(Number(yearlyOptionSku) * (Number(column.flow_perc) / 100));
                } else {
                    calculatedRow.sku_plans[key] = 0;
                }
            }
        });

        // Add the missing sku plans to the biggest flow percentage column by
        // ordering the resulting values by flow perc and selecting the highest.
        // Then Subtract the current sku total for all columns from the yearly sku,
        // and add the remaining balance to the highest flow perc column
        const columnsInOrderOfHighestFlowPerc: Array<IMonthlyOptionColumn> = orderBy(columns, ['flow_perc'], ['desc']);
        const highestFlowPercColumnName: string = columnsInOrderOfHighestFlowPerc[0].column_name;
        calculatedRow.sku_plans[highestFlowPercColumnName] =
            calculatedRow.sku_plans[highestFlowPercColumnName] +
            (Number(yearlyOptionSku) - this.getSkuPlansTotal(calculatedRow.sku_plans));

        return calculatedRow;
    }

    public getSkuPlansTotal(skuPlans: Record<string, number>): number {
        const skuPlanValues: Array<number> = Object.entries(skuPlans)
            .filter(([key]: [string, number]) => !['sku_plan', 'Total'].includes(key))
            .map(([_, value]: [string, number]): number => value);

        const skuPlanSum: number = skuPlanValues.reduce((skuOne: number, skuTwo: number) => skuOne + skuTwo, 0);

        return Number(skuPlanSum);
    }

    public getSubTimeOptionActualPercentage(subTimeOptions: Array<ISubTimeOptionActualPercentageRequestModel>): number {
        if (subTimeOptions.length === 0) {
            return 0;
        }
        const filteredSubTimeOptions: Array<ISubTimeOptionActualPercentageRequestModel> = subTimeOptions.filter(
            (option: ISubTimeOptionActualPercentageRequestModel) => option.optionsCount > 0 && option.parentOptionCount > 0
        );

        const summedPercentage: number = filteredSubTimeOptions.reduce(
            (acc: number, option: ISubTimeOptionActualPercentageRequestModel) =>
                acc + (option.parentOptionCount > 0 ? option.optionsCount / option.parentOptionCount : 0),
            0
        );

        const nrOptionsWithValues: number = filteredSubTimeOptions.length;
        if (nrOptionsWithValues === 0) {
            return 0;
        }

        const calculatedActualPercentage: number = (summedPercentage / nrOptionsWithValues) * 100;

        return Math.round((calculatedActualPercentage + Number.EPSILON) * 100) / 100;
    }

    public getSubTimeOptionBudget(
        // fashionBudget: number,
        subTimeOptions: Array<ISubTimeOptionActualPercentageRequestModel>
    ): number {
        if (subTimeOptions.length === 0) {
            return 0;
        }

        const summedBudgets: number = subTimeOptions.reduce((acc: number, option: ISubTimeOptionActualPercentageRequestModel) => {
            const optionsPercentage: number = option.parentOptionCount > 0 ? option.optionsCount / option.parentOptionCount : 0;
            return acc + optionsPercentage * option.budget;
        }, 0);

        // const nrOptionsWithValues: number = subTimeOptions.filter(
        //     (option: ISubTimeOptionActualPercentageRequestModel) => option.optionsCount > 0 && option.budget > 0
        // ).length;
        // const calculatedActualPercentage: number = (summedBudgets / nrOptionsWithValues) * 100;

        return Math.round((summedBudgets + Number.EPSILON) * 100) / 100;
        // const calculatedBudget: number = (fashionBudget * this.getSubTimeOptionActualPercentage(subTimeOptions)) / 100;
        // return Math.round((calculatedBudget + Number.EPSILON) * 100) / 100;
    }

    public calculateOptions(
        attributeValue: string,
        currentApproval: IOptionsApprovalResponseModel,
        lastApproval: IOptionsApprovalResponseModel
    ): IOptionsApprovalResponseModel {
        if (currentApproval && lastApproval) {
            if (attributeValue === 'ALL') {
                this.calculateOptionsForAll(currentApproval, lastApproval);
            } else {
                this.calculateOptionsForSpecificBrand(currentApproval, lastApproval);
            }
        }
        return currentApproval;
    }

    private calculateOptionsForAll(
        currentApproval: IOptionsApprovalResponseModel,
        lastApproval: IOptionsApprovalResponseModel
    ): void {
        const optionsCountTotal: number = flatMap(
            currentApproval.attributeValues,
            (currentAttributeValue: IOptionsAttributeValueResponseModel) => currentAttributeValue.options
        )
            .filter((option: IOptionResponseModel) => option.productLevelId !== 'Total')
            .reduce((sum: number, option: IOptionResponseModel) => sum + option.optionsCount, 0);
        // const totalBudget: number = currentApproval.attributeValues.reduce(
        //     (sum: number, currentAttributeValue: IOptionsAttributeValueResponseModel) => {
        //         const currentBudget: IBudgetResponseModel = currentAttributeValue.budget;
        //         return sum + currentBudget.totalBudget * (currentBudget.fashionPercentage / 100);
        //     },
        //     0
        // );

        currentApproval.attributeValues.forEach((currentAttributeValue: IOptionsAttributeValueResponseModel) => {
            const lastOptions: Array<IOptionResponseModel> = lastApproval.attributeValues.find(
                (t: IOptionsAttributeValueResponseModel) => t.attributeValue === currentAttributeValue.attributeValue
            )?.options;

            if (!lastOptions) {
                throw new Error('Matching AttributeValue not found in lastApproval.');
            }

            // Remove the "Total" row if present, since we're pushing it at the end of this calculation
            const currentOptions: Array<IOptionResponseModel> = currentAttributeValue.options.filter(
                (option: IOptionResponseModel) => option.productLevelId !== 'Total'
            );

            currentOptions.forEach((currentOption: IOptionResponseModel) => {
                const lastOption: IOptionResponseModel = lastOptions.find(
                    (t: IOptionResponseModel) => t.productLevelId === currentOption.productLevelId
                );
                if (!lastOption) {
                    throw new Error('Could not find ' + currentOption.productLevelId);
                }
                const salesMultiplier: number = Math.max(currentOption.minimumOrderQuantity ?? 0, lastOption.averageUnits ?? 0);
                const unitSales: number = currentOption.optionsCount * salesMultiplier;
                currentOption.optionShareOfBusiness =
                    optionsCountTotal === 0 ? 0 : (currentOption.optionsCount / optionsCountTotal) * 100;
                currentOption.unitsSales = unitSales;
                const markUp: number = 1 + (currentOption.priceMarkUpPercentage ?? 0) / 100;
                currentOption.valueWholesalePriceTotal = unitSales * (lastOption.averageWholesalePrice ?? 0)
                    * markUp;
            });
        });
        const allCurrentOptions: Array<IOptionResponseModel> = flatMap(
            currentApproval.attributeValues,
            (currentAttributeValue: IOptionsAttributeValueResponseModel) => currentAttributeValue.options
        ).filter((option: IOptionResponseModel) => option.productLevelId !== 'Total');
        const unitSalesTotal: number = allCurrentOptions.reduce(
            (sum: number, row: IOptionResponseModel) => sum + row.unitsSales,
            0
        );
        const valueWspTotal: number = allCurrentOptions.reduce(
            (sum: number, row: IOptionResponseModel) => sum + row.valueWholesalePriceTotal,
            0
        );
        currentApproval.attributeValues.forEach((currentAttributeValue: IOptionsAttributeValueResponseModel) => {
            const currentOptions: Array<IOptionResponseModel> = currentAttributeValue.options.filter(
                (option: IOptionResponseModel) => option.productLevelId !== 'Total'
            );

            currentOptions.forEach((currentOption: IOptionResponseModel) => {
                const budget: IBudgetResponseModel = currentAttributeValue.budgets.find(
                    (x: IBudgetResponseModel) => x.productLevelId === currentOption.productLevelId
                );
                const fashionBudget: number = budget.totalBudget * (budget.fashionPercentage / 100);

                currentOption.unitsShareOfBusiness = unitSalesTotal === 0 ? 0 : (currentOption.unitsSales / unitSalesTotal) * 100;
                currentOption.valueShareOfBusiness =
                    valueWspTotal === 0 ? 0 : (currentOption.valueWholesalePriceTotal / valueWspTotal) * 100;
                currentOption.valueBudget =
                    valueWspTotal === 0 ? 0 : fashionBudget;
                currentOption.valueVariance =
                    valueWspTotal === 0
                        ? 0
                        : currentOption.valueWholesalePriceTotal -
                          fashionBudget;
            });
        });

    }

    private calculateOptionsForSpecificBrand(
        currentApproval: IOptionsApprovalResponseModel,
        lastApproval: IOptionsApprovalResponseModel
    ): void {
        currentApproval.attributeValues.forEach((currentAttributeValue: IOptionsAttributeValueResponseModel) => {
            // const currentBudget: IBudgetResponseModel = currentAttributeValue.budget;
            // const totalBudget: number = (currentBudget.totalBudget * currentBudget.fashionPercentage) / 100;

            const lastOptions: Array<IOptionResponseModel> = lastApproval.attributeValues.find(
                (t: IOptionsAttributeValueResponseModel) => t.attributeValue === currentAttributeValue.attributeValue
            )?.options;

            if (!lastOptions) {
                throw new Error('Matching AttributeValue not found in lastApproval.');
            }

            // Remove the "Total" row if present, since we're pushing it at the end of this calculation
            const currentOptions: Array<IOptionResponseModel> = currentAttributeValue.options.filter(
                (option: IOptionResponseModel) => option.productLevelId !== 'Total'
            );
            const optionsCountTotal: number = currentOptions.reduce(
                (sum: number, option: IOptionResponseModel) => sum + option.optionsCount,
                0
            );

            currentOptions.forEach((currentOption: IOptionResponseModel) => {
                const lastOption: IOptionResponseModel = lastOptions.find(
                    (t: IOptionResponseModel) => t.productLevelId === currentOption.productLevelId
                );
                if (!lastOption) {
                    throw new Error('Could not find ' + currentOption.productLevelId);
                }
                const salesMultiplier: number = Math.max(currentOption.minimumOrderQuantity ?? 0, lastOption.averageUnits ?? 0);
                const unitSales: number = currentOption.optionsCount * salesMultiplier;
                currentOption.optionShareOfBusiness =
                    optionsCountTotal === 0 ? 0 : (currentOption.optionsCount / optionsCountTotal) * 100;
                currentOption.unitsSales = unitSales;
                const markUpPercentage: number = (currentOption.priceMarkUpPercentage ?? 0) / 100;
                const markUp: number = 1 + markUpPercentage;
                currentOption.valueWholesalePriceTotal = unitSales * (lastOption.averageWholesalePrice ?? 0)
                    * markUp;
            });

            const unitSalesTotal: number = currentOptions.reduce(
                (sum: number, row: IOptionResponseModel) => sum + row.unitsSales,
                0
            );
            const valueWspTotal: number = currentOptions.reduce(
                (sum: number, row: IOptionResponseModel) => sum + row.valueWholesalePriceTotal,
                0
            );

            currentOptions.forEach((currentOption: IOptionResponseModel) => {
                const budget: IBudgetResponseModel = currentAttributeValue.budgets.find(
                    (x: IBudgetResponseModel) => x.productLevelId === currentOption.productLevelId
                );
                const fashionBudget: number = budget.totalBudget * (budget.fashionPercentage / 100);
                currentOption.unitsShareOfBusiness = unitSalesTotal === 0 ? 0 : (currentOption.unitsSales / unitSalesTotal) * 100;
                currentOption.valueShareOfBusiness =
                    valueWspTotal === 0 ? 0 : (currentOption.valueWholesalePriceTotal / valueWspTotal) * 100;
                currentOption.valueBudget =
                    valueWspTotal === 0 ? 0 : fashionBudget;
                currentOption.valueVariance =
                    valueWspTotal === 0
                        ? 0
                        : currentOption.valueWholesalePriceTotal -
                          fashionBudget;
            });

            const totalRow: IOptionResponseModel = {
                id: 0,
                optionsCount: currentOptions.reduce((sum: number, row: IOptionResponseModel) => sum + row.optionsCount, 0),
                optionShareOfBusiness: currentOptions.reduce(
                    (sum: number, row: IOptionResponseModel) => sum + row.optionShareOfBusiness,
                    0
                ),
                unitsSales: currentOptions.reduce((sum: number, row: IOptionResponseModel) => sum + row.unitsSales, 0),
                valueWholesalePriceTotal: currentOptions.reduce(
                    (sum: number, row: IOptionResponseModel) => sum + row.valueWholesalePriceTotal,
                    0
                ),
                unitsShareOfBusiness: currentOptions.reduce(
                    (sum: number, row: IOptionResponseModel) => sum + row.unitsShareOfBusiness,
                    0
                ),
                valueShareOfBusiness: currentOptions.reduce(
                    (sum: number, row: IOptionResponseModel) => sum + row.valueShareOfBusiness,
                    0
                ),
                valueBudget: currentOptions.reduce((sum: number, row: IOptionResponseModel) => sum + row.valueBudget, 0),
                valueVariance:
                    currentOptions.reduce((sum: number, row: IOptionResponseModel) => sum + row.valueWholesalePriceTotal, 0) -
                    currentOptions.reduce((sum: number, row: IOptionResponseModel) => sum + row.valueBudget, 0),
                productLevelId: 'Total',
                productLevelName: '',
                subTimeOptions: []
            };

            currentOptions.push(totalRow);
        });
    }
}
